import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "client";
import { ApiResponseType, ModulePrefixes, NotificationType } from "enums";
import { axiosInstance, useUtilities } from "helpers";
import { t } from "i18next";
import { useEffect } from "react";
import {
  getAllTemplateGlossary,
  addTemplateGlossary,
  deleteTemplateGlossary,
  updateTemplateGlossary,
  getAllDefaultSiteGlossary,
  addDefaultSiteGlossary,
  deleteDefaultSiteGlossary,
  markDataStale,
} from "redux/reducers/templateLibrary/glossary";

export async function getTemplateGlossary() {
  return axiosInstance
    .get("allglossary", {
      params: {
        api_key: localStorage.getItem("apikey"),
      },
    })
    .then((res) => res.data)
    .catch((err) => err);
}

export function useGetTemplateGlossary() {
  return useQuery(["templateGlossary"], getTemplateGlossary);
}

export function useGlosssary() {
  const {
    dispatch,
    glossary: { loaded, glossary },
  } = useUtilities();
  const { data: templateGlossary, error, isLoading, isRefetching } = useGetTemplateGlossary();

  useEffect(() => {
    if (isLoading || loaded || isRefetching) return;
    if (templateGlossary) {
      dispatch(getAllTemplateGlossary(templateGlossary.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateGlossary, isLoading, loaded, isRefetching]);
  return { data: glossary, isLoading, error };
}

export async function addTemplateGlossaryApi({
  glossary_title,
  glossary_body,
  glossary_integration,
  tags,
  category_id,
}) {
  return axiosInstance
    .post("addglossary", {
      api_key: localStorage.getItem("apikey"),
      glossary_title,
      glossary_body,
      glossary_integration,
      tags,
      category_id,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useAddGlossary(redirect = true) {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(addTemplateGlossaryApi, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(addTemplateGlossary(data.data));
        notification({
          type: "success",
          message: t("libraryTemplate.glossary.api.success.added"),
        });
        if (redirect) {
          navigate(`/${ModulePrefixes.TEMPLATE_LIBRARY}/glossary/${data.data.id}`);
        }
        dispatch(markDataStale())
        queryClient.invalidateQueries(["templateGlossary"]);
        queryClient.invalidateQueries(["defaultSiteGlossary"]);
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: err.message,
      });
    },
  });
}

export async function updateTemplateGlossaryApi({
  glossary_title,
  glossary_body,
  glossary_integration,
  tags,
  glossary_id,
  category_id,
}) {
  return axiosInstance
    .post("updateglossary", {
      api_key: localStorage.getItem("apikey"),
      glossary_title,
      glossary_body,
      glossary_integration,
      tags,
      glossary_id,
      category_id,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useUpdateGlossary() {
  const { dispatch, notification } = useUtilities();
  return useMutation(updateTemplateGlossaryApi, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(updateTemplateGlossary(data.data));
        notification({
          type: "success",
          message: t("libraryTemplate.glossary.api.success.updated"),
        });
        dispatch(markDataStale())
        queryClient.invalidateQueries(["templateGlossary"]);
        queryClient.invalidateQueries(["defaultSiteGlossary"]);
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: err.message,
      });
    },
  });
}

export async function deleteTemplateGlossaryApi(glossary_id) {
  return axiosInstance
    .post("delglossary", {
      api_key: localStorage.getItem("apikey"),
      glossary_id,
    })
    .then((res) => ({ ...res.data, glossary_id }))
    .catch((err) => new Error(err));
}

export function useDeleteGlossary(redirect = true) {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(deleteTemplateGlossaryApi, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(deleteTemplateGlossary(data.glossary_id));
        notification({
          type: "success",
          message: t("libraryTemplate.glossary.api.success.deleted"),
        });
        if (redirect) {
          navigate(`/${ModulePrefixes.TEMPLATE_LIBRARY}/glossary`);
        }
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: err.message,
      });
    },
  });
}

export async function getDefaultSiteGlossary() {
  return axiosInstance
    .post("getwpslglossarytemp", {
      api_key: localStorage.getItem("apikey"),
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useGetDefaultGlossary() {
  return useQuery(["defaultSiteGlossary"], getDefaultSiteGlossary);
}

export function useDefaultGlossary() {
  const {
    dispatch,
    glossary: {
      defaultSite: { loaded, glossary },
    },
  } = useUtilities();
  const { data: defaultGlossary, error, isLoading, isRefetching } = useGetDefaultGlossary();

  useEffect(() => {
    if (isLoading || loaded || isRefetching) return;
    if (defaultGlossary) {
      dispatch(getAllDefaultSiteGlossary(defaultGlossary.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultGlossary, isLoading, loaded, isRefetching]);

  return { data: glossary, isLoading: isLoading && !loaded, error };
}

export async function addDefaultGlossary(id) {
  return axiosInstance
    .post("adddefaulttemplateglossary", {
      api_key: localStorage.getItem("apikey"),
      id,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useAddDefaultGlossary() {
  const { notification, dispatch } = useUtilities();
  return useMutation(addDefaultGlossary, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(addDefaultSiteGlossary(data.data));
        notification({
          type: "success",
          message: t("libraryTemplate.glossary.api.success.added"),
        });
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: err.message,
      });
    },
  });
}

export async function deleteDefaultGlossary(id) {
  return axiosInstance
    .post("defaultglossaryremove", {
      api_key: localStorage.getItem("apikey"),
      id,
    })
    .then((res) => ({ ...res.data, id }))
    .catch((err) => new Error(err));
}

export function useDeleteDefaultGlossary() {
  const { dispatch, notification } = useUtilities();
  return useMutation(deleteDefaultGlossary, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(deleteDefaultSiteGlossary(data.id));
        notification({
          type: "success",
          message: t("libraryTemplate.glossary.api.success.deleted"),
        });
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: err.message,
      });
    },
  });
}

async function addGlossaryCategory(title) {
  return axiosInstance
    .post("addglossarycategory", {
      title,
      api_key: localStorage.getItem("apikey"),
      website_id: null,
    })
    .then((res) => res.data)
    .catch((err) => err);
}

export function useAddGlossaryCategory() {
  const { navigate, notification } = useUtilities();
  return useMutation(addGlossaryCategory, {
    onSuccess: (res) => {
      if (res.status_code === ApiResponseType.SUCCESS) {
        queryClient.invalidateQueries(["glossaryCategory"]);
        notification({
          type: NotificationType.SUCCESS,
          message: t("libraryTemplate.glossaryCategories.api.success.added"),
        });
        navigate(`/${ModulePrefixes.TEMPLATE_LIBRARY}/glossary/categories/${res.data.id}`);
      } else {
        notification({
          type: NotificationType.ERROR,
          message: t("libraryTemplate.glossaryCategories.api.error.failedToAdd"),
        });
      }
    },
    onError: (error) => {
      notification({
        type: NotificationType.ERROR,
        message: t("libraryTemplate.glossaryCategories.api.error.failedToAdd"),
      });
    },
  });
}

async function updateGlossaryCategory(data) {
  return axiosInstance
    .post("updateglossarycategory", {
      api_key: localStorage.getItem("apikey"),
      website_id: null,
      ...data,
    })
    .then((res) => res.data)
    .catch((err) => err);
}

export function useUpdateGlossaryCategory() {
  const { notification } = useUtilities();
  return useMutation(updateGlossaryCategory, {
    onSuccess: (res) => {
      if (res.status_code === ApiResponseType.SUCCESS) {
        notification({
          type: NotificationType.SUCCESS,
          message: t("libraryTemplate.glossaryCategories.api.success.updated"),
        });
        queryClient.invalidateQueries(["glossaryCategory"]);
      } else {
        notification({
          type: NotificationType.ERROR,
          message: t("libraryTemplate.glossaryCategories.api.error.failedToUpdated"),
        });
      }
    },
    onError: (error) => {
      notification({
        type: NotificationType.ERROR,
        message: t("libraryTemplate.glossaryCategories.api.error.failedToUpdated"),
      });
    },
  });
}

async function deleteGlossaryCategory(id) {
  return axiosInstance
    .post("delcategory", {
      api_key: localStorage.getItem("apikey"),
      category_id: id,
    })
    .then((res) => res.data)
    .catch((err) => err);
}

export function useDeleteGlossaryCategory() {
  const { notification } = useUtilities();
  return useMutation(deleteGlossaryCategory, {
    onSuccess: (res) => {
      if (res.status_code === ApiResponseType.SUCCESS) {
        notification({
          type: NotificationType.SUCCESS,
          message: t("libraryTemplate.glossaryCategories.api.success.deleted"),
        });
        queryClient.invalidateQueries(["glossaryCategory"]);
      } else {
        notification({
          type: NotificationType.ERROR,
          message: res.message ?? t("libraryTemplate.glossaryCategories.api.error.failedToDelete"),
        });
      }
    },
    onError: () => {
      notification({
        type: NotificationType.ERROR,
        message: t("libraryTemplate.glossaryCategories.api.error.failedToDelete"),
      });
    },
  });
}

async function getGlossaryCategory() {
  return axiosInstance
    .get("allcategories", {
      params: {
        api_key: localStorage.getItem("apikey"),
      },
    })
    .then((res) => res.data)
    .catch((err) => err);
}

export function useGetGlossaryCategory() {
  return useQuery(["glossaryCategory"], getGlossaryCategory, {
    select: (res) => res.data,
  });
}
